import { useState } from "react";
import { Play, ArrowRight, Sparkles, GraduationCap, Heart, Building2 } from "lucide-react";
import TrustBadgeBand from "@/components/TrustBadgeBand";

const AUDIENCES = [
  { key: "student",  label: "Student",  icon: GraduationCap },
  { key: "educator", label: "Educator", icon: Sparkles },
  { key: "parent",   label: "Parent",   icon: Heart },
  { key: "sponsor",  label: "Sponsor",  icon: Building2 },
];

const TRUST_PILLS = [
  "K12 Ready",
  "AI-Powered Learning",
  "Leadership Development",
  "Entrepreneurship Education",
];

/**
 * HeroSection — cinematic two-column hero. Copy on the left, cinematic
 * feature image on the right (kids + LemonBot at the lemonade stand).
 * On mobile the image appears ABOVE the copy block (order-first).
 */
export default function HeroSection({ onPrimary, onDemo, onBookDemo }) {
  const [audience, setAudience] = useState("student");
  const Audience = AUDIENCES.find((a) => a.key === audience) || AUDIENCES[0];

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-[#FDFBF7] pt-12 sm:pt-16 pb-20 sm:pb-28"
      data-testid="hero-section"
    >
      {/* Decorative glow orbs */}
      <div aria-hidden="true" className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full bg-[#FFD500]/40 blur-3xl lsbp-pulse-glow" />
      <div aria-hidden="true" className="absolute -bottom-40 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#00E65C]/30 blur-3xl lsbp-pulse-glow" style={{ animationDelay: "1.5s" }} />
      <span aria-hidden="true" className="absolute top-24 right-[6%] text-4xl lsbp-float-slow select-none hidden xl:block">🍋</span>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-6 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-7 text-center lg:text-left">
          <p className="text-[11px] sm:text-xs font-black tracking-[0.22em] uppercase text-gray-600 mb-5">
            K-12 Entrepreneurship · Responsible AI · Future-Ready Learning
          </p>

          {/* Audience selector */}
          <div
            role="tablist"
            aria-label="Choose your perspective"
            className="inline-flex items-center bg-white/80 backdrop-blur ring-1 ring-black/10 rounded-full p-1 mb-7 shadow-[0_4px_14px_rgba(0,0,0,0.06)]"
            data-testid="hero-audience-selector"
          >
            {AUDIENCES.map((a) => {
              const active = a.key === audience;
              return (
                <button
                  key={a.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setAudience(a.key)}
                  className={`relative inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all ${
                    active ? "bg-black text-white shadow" : "text-gray-700 hover:text-black"
                  }`}
                  data-testid={`hero-audience-${a.key}`}
                >
                  <a.icon className="w-3.5 h-3.5" />
                  {a.label}
                </button>
              );
            })}
          </div>

          <h1
            className="text-[38px] sm:text-5xl lg:text-[64px] font-black tracking-tighter leading-[1.02] text-black"
            data-testid="hero-headline"
          >
            Build <span className="lsbp-yellow-chip">Big Ideas.</span>
            <br /> Learn Business.{" "}
            <span className="lsbp-green-chip">Lead The Future.</span>
          </h1>

          <p
            className="mt-6 text-base md:text-xl text-gray-700 leading-relaxed max-w-xl mx-auto lg:mx-0"
            data-testid="hero-subheadline"
          >
            An interactive K-12 entrepreneurship platform that teaches students
            business, leadership, teamwork, financial literacy, and responsible
            AI through storytelling and real-world learning.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3" data-testid="hero-cta-row">
            <button
              onClick={() => onPrimary(audience)}
              className="group inline-flex items-center justify-center gap-2 bg-black text-white px-7 py-4 rounded-full text-lg font-bold shadow-[0_8px_24px_rgba(0,0,0,0.18)] hover:bg-[#FFD500] hover:text-black transition-all w-full sm:w-auto whitespace-nowrap"
              data-testid="hero-cta-primary"
            >
              Start Learning
              <Audience.icon className="w-4 h-4" />
              <span>as {Audience.label}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={onDemo}
              className="inline-flex items-center justify-center gap-2 bg-white ring-2 ring-black text-black px-6 py-4 rounded-full text-lg font-bold hover:bg-[#FFD500] transition-colors w-full sm:w-auto"
              data-testid="hero-cta-demo"
            >
              <Play className="w-4 h-4" /> View Demo
            </button>
            <button
              onClick={onBookDemo}
              className="inline-flex items-center justify-center gap-2 bg-transparent ring-2 ring-gray-300 text-gray-800 px-6 py-4 rounded-full text-lg font-bold hover:ring-[#00E65C] hover:text-black transition-colors w-full sm:w-auto"
              data-testid="hero-cta-book-demo"
            >
              Book A School Demo
            </button>
          </div>

          <ul
            className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-[11px] sm:text-xs font-bold tracking-wider uppercase text-gray-600"
            data-testid="hero-trust-pills"
          >
            {TRUST_PILLS.map((p, i) => (
              <li key={p} className="flex items-center gap-2">
                {i > 0 && <span aria-hidden="true" className="text-gray-300">•</span>}
                <span>{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex justify-center lg:justify-start">
            <TrustBadgeBand testId="hero-trust-band" />
          </div>
        </div>

        {/* RIGHT — cinematic feature image */}
        <div className="lg:col-span-5 relative order-first lg:order-last">
          <div className="relative rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_30px_60px_rgba(0,0,0,0.18)] bg-white">
            <img
              src="/assets/landing/hero-students.png"
              alt="Students collaborating at a lemonade stand with LemonBot while building business ideas on laptops and tablets."
              className="w-full h-auto block"
              loading="eager"
              fetchPriority="high"
              data-testid="hero-feature-image"
            />
          </div>
          <span aria-hidden="true" className="absolute -top-3 -right-3 text-3xl lsbp-float select-none">✨</span>
        </div>
      </div>
    </section>
  );
}
